import { Request, Response, Server } from 'miragejs';

import LocalStorageManager from 'mocks/server/localStorageManager';
import { handleDraftEntity } from 'mocks/server/utils/draftHandler';
import {
  getDraftEntities,
  removeEntities,
  setStatusForEntities,
} from 'mocks/server/utils/entityUtils';
import { paged } from 'mocks/server/utils/paged';
import { IBaseEntity, IDraftFrame, IDraftLinkedAsset } from 'types/detailsEntityType';
import { EntityType } from 'types/entityType';
import {
  DRAFTS_API_URL,
  DRAFTS_COUNT_API_URL,
  DRAFTS_ERROR_API_URL,
  DRAFTS_EXPORT_API_URL,
  DRAFTS_READY_API_URL,
  DRAFTS_SYNC_API_URL,
  DRAFTS_SYNCING_API_URL,
  TASKS_API_URL,
} from 'utils/defaults';
import { DraftStatus } from 'utils/statuses';

export const draftsRoutes = (
  server: Server,
  draftFrameStore: LocalStorageManager<IDraftFrame>,
  draftLinkedAssetStore: LocalStorageManager<IDraftLinkedAsset>,
  removedEntities: LocalStorageManager<IBaseEntity>
) => {
  server.get(DRAFTS_COUNT_API_URL, () => {
    const draftFramesCount = draftFrameStore.getAll().length;
    const draftLinkedAssetsCount = draftFrameStore.getAll().length;
    return {
      count: draftFramesCount + draftLinkedAssetsCount,
    };
  });

  const handleExportGet = (_schema: unknown, request: Request) => {
    const filterKeys = ['environments', 'mediaOwnerNames', 'formatSizeNames', 'spaceIds'];
    const headers = filterKeys.join(',');
    const values = filterKeys
      .map((key) => {
        const val = request.queryParams[key] || '';
        return val.includes(',') ? `"${val}"` : val;
      })
      .join(',');

    const csv = [headers, values].join('\n');

    return new Response(
      200,
      {
        'Content-Type': 'application/csv',
        'X-Filename': 'drafts.csv',
      },
      csv
    );
  };

  server.get(`${DRAFTS_EXPORT_API_URL}/ready`, handleExportGet);
  server.get(`${DRAFTS_EXPORT_API_URL}/error`, handleExportGet);

  server.get(`${DRAFTS_API_URL}/:spaceId`, (schema, request) => {
    const spaceId = request.params.spaceId;
    const frame = draftFrameStore.getById(spaceId);
    const linkedAsset = draftLinkedAssetStore.getById(spaceId);

    if (frame) {
      return handleDraftEntity(frame, EntityType.FRAME);
    } else if (linkedAsset) {
      return handleDraftEntity(linkedAsset, EntityType.LINKED_ASSET);
    }
    return new Response(404, {}, { error: 'No draft entity found' });
  });

  server.get(DRAFTS_SYNCING_API_URL, (schema, request) => {
    const page = Number(request.queryParams.page);
    const pageSize = Number(request.queryParams.pageSize);
    const syncingDrafts = getDraftEntities(
      draftFrameStore,
      draftLinkedAssetStore,
      DraftStatus.SYNCING
    );

    if (page && pageSize) {
      return paged(syncingDrafts, page, pageSize);
    }
    return paged(syncingDrafts, 1, 10);
  });

  server.get(DRAFTS_READY_API_URL, (schema, request) => {
    const page = Number(request.queryParams.page);
    const pageSize = Number(request.queryParams.pageSize);
    const readyDrafts = getDraftEntities(draftFrameStore, draftLinkedAssetStore, DraftStatus.READY);

    if (page && pageSize) {
      return paged(readyDrafts, page, pageSize);
    }
    return paged(readyDrafts, 1, 10);
  });

  server.get(DRAFTS_ERROR_API_URL, (schema, request) => {
    const page = Number(request.queryParams.page);
    const pageSize = Number(request.queryParams.pageSize);
    const errorDrafts = getDraftEntities(draftFrameStore, draftLinkedAssetStore, DraftStatus.ERROR);
    const incompleteDrafts = getDraftEntities(
      draftFrameStore,
      draftLinkedAssetStore,
      DraftStatus.INCOMPLETE
    );
    const invalidDrafts = getDraftEntities(
      draftFrameStore,
      draftLinkedAssetStore,
      DraftStatus.INVALID
    );
    const allErrorDrafts = errorDrafts.concat(incompleteDrafts).concat(invalidDrafts);

    if (page && pageSize) {
      return paged(allErrorDrafts, page, pageSize);
    }
    return paged(allErrorDrafts, 1, 10);
  });

  server.get(TASKS_API_URL, () => {
    return {
      tasksProcessing: false,
    };
  });

  server.post(DRAFTS_SYNC_API_URL, (schema, request) => {
    const spaceIds = request.queryParams.spaceIds;
    const ids = spaceIds
      ? String(spaceIds).split(',')
      : getDraftEntities(draftFrameStore, draftLinkedAssetStore, DraftStatus.READY).map(
          (entity) => entity.spaceId
        );
    setStatusForEntities(ids, draftFrameStore, DraftStatus.SYNCING);
    setStatusForEntities(ids, draftLinkedAssetStore, DraftStatus.SYNCING);
    console.log('Successful syncing for IDs: ', ids);
    return new Response(204, {}, {});
  });

  server.delete(DRAFTS_API_URL, (schema, request) => {
    const data = request.requestBody;
    const ids = JSON.parse(data);
    removeEntities(ids, draftFrameStore, removedEntities);
    removeEntities(ids, draftLinkedAssetStore, removedEntities);
    console.log('Successful deletion for IDs: ', data);
    return new Response(204, {}, {});
  });
};
